import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Activity, Check, Ban, Sparkles } from "lucide-react";

const ACTION_STATUS = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700" },
  executed: { label: "Exécutée", cls: "bg-green-50 text-green-700" },
  rejected: { label: "Rejetée", cls: "bg-red-50 text-red-600" },
};

const KIND_LABELS = {
  agent_chat: "Assistant personnel",
  post_to_offer: "Publication → brouillon d'offre",
  goal_to_tasks: "Objectif → brouillon de tâches",
  explain_match: "Explication de matching",
  image: "Génération d'image",
};

export default function AgentActivity() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/agent/activity");
      setData(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (actionId, decision) => {
    setError("");
    try {
      await api.post(`/agent/actions/${actionId}/decide`, { decision });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="agent-activity-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <h1 className="text-xl font-bold text-brand-navy flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand-turquoise" />
          Activité IA
        </h1>
        <p className="text-sm text-slate-500">
          Transparence totale : chaque proposition de l'IA est journalisée, et aucune action n'est exécutée sans votre approbation.
        </p>

        {error && <div data-testid="activity-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}

        <section>
          <h2 className="font-semibold text-brand-navy mb-3">Actions proposées par l'assistant ({data?.actions?.length || 0})</h2>
          <div className="space-y-2">
            {(data?.actions || []).map((a) => {
              const ui = ACTION_STATUS[a.status] || ACTION_STATUS.pending;
              return (
                <div key={a.action_id} data-testid={`activity-action-${a.action_id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-navy">{a.label}</p>
                    <p className="text-xs text-slate-400">
                      {a.type} · {a.created_at ? new Date(a.created_at).toLocaleString("fr-CA") : "—"}
                      {a.result ? ` · ${a.result}` : ""}
                    </p>
                  </div>
                  {a.status === "pending" ? (
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`activity-approve-${a.action_id}`}
                        onClick={() => decide(a.action_id, "approved")}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                      >
                        <Check className="w-3.5 h-3.5" /> Approuver
                      </button>
                      <button
                        data-testid={`activity-reject-${a.action_id}`}
                        onClick={() => decide(a.action_id, "rejected")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                      >
                        <Ban className="w-3.5 h-3.5" /> Rejeter
                      </button>
                    </div>
                  ) : (
                    <span data-testid={`activity-status-${a.action_id}`} className={`text-xs font-medium rounded-full px-3 py-1 ${ui.cls}`}>{ui.label}</span>
                  )}
                </div>
              );
            })}
            {data && data.actions.length === 0 && (
              <p data-testid="activity-actions-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
                Aucune action proposée. Demandez quelque chose à l'assistant (icône en bas à droite).
              </p>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-semibold text-brand-navy mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-orange" />
            Journal IA ({data?.logs?.length || 0})
          </h2>
          <div className="space-y-2">
            {(data?.logs || []).map((log) => (
              <div key={log.log_id} data-testid={`activity-log-${log.log_id}`} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-navy">{KIND_LABELS[log.kind] || log.kind}</p>
                  <p className="text-xs text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString("fr-CA") : "—"} · {log.model}</p>
                </div>
                <span className="text-xs font-semibold bg-brand-sky text-brand-navy rounded-full px-3 py-1 shrink-0">{log.mode}</span>
              </div>
            ))}
            {data && data.logs.length === 0 && (
              <p data-testid="activity-logs-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
                Aucune activité IA pour le moment.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
